import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { Subscription } from 'rxjs';
import { EmdHierarchicalGridComponent } from '../charts/emd-hierarchical-grid/emd-hierarchical-grid.component';
import { EmdFilterService } from '../emd-filter.service';
import { EmdSelectionService } from '../emd-selection.service';
import {
  EMD_COLORS,
  EmdInventoryData,
  INVENTORY_DEFAULT,
  INVENTORY_METRICS,
  deriveInventory,
} from '../emd-mock-data';

Chart.register(...registerables);

@Component({
    selector: 'emd-inventory-tab',
    templateUrl: './emd-inventory-tab.component.html',
    styleUrls: ['./emd-inventory-tab.component.scss'],
    standalone: false
})
export class EmdInventoryTabComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('dohChart') dohChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild(EmdHierarchicalGridComponent) grid?: EmdHierarchicalGridComponent;

  readonly colors = EMD_COLORS;

  /** Latest derivation; the KPIs, both charts and the grid read out of this. */
  data: EmdInventoryData = INVENTORY_DEFAULT;

  kpiCards = this.data.kpis;
  metrics = INVENTORY_METRICS;
  selectedMetric = 'Avg. DOH';
  searchText = '';

  inventoryRows = this.data.rows;
  inventoryColumnDefs: any[] = [];
  inventoryChildColumnDefs: any[] = [];

  /** Staged cross-filter selection previewed on the grid (pending). */
  selectedWholesalers: string[] = [];

  /** Staged period selection — those balance bars stay solid. */
  selectedPeriods: string[] = [];

  private dohChartInstance: Chart | null = null;
  private readonly subs: Subscription[] = [];

  constructor(
    private filters: EmdFilterService,
    private selection: EmdSelectionService
  ) {}

  // ── Dispense and Inventory Balance ──────────────────────────────────────────
  // DOH bars sit on the right axis (days), the two quantity series on the left.

  balanceBarData: { period: string; value: number }[] = [];
  balanceLineData: { period: string; series: string; value: number }[] = [];
  readonly balanceBarColor = EMD_COLORS.doh;
  readonly balanceLineColors = {
    'QTY ON HAND': EMD_COLORS.onHand,
    'QTY SOLD': EMD_COLORS.sold,
  };

  ngOnInit(): void {
    this.buildGridColumns();
    this.refresh();
    this.subs.push(
      this.filters.changes.subscribe((tab) => {
        if (tab === 'Inventory') { this.refresh(); }
      })
    );
    this.subs.push(
      this.selection.changes.subscribe(() => {
        this.selectedWholesalers = [...this.selection.effective('wholesaler')];
        this.selectedPeriods = [...this.selection.effective('period')];
      })
    );
  }

  onRowSelect(event: { wholesaler: string; ctrlKey: boolean }): void {
    this.selection.stage('wholesaler', event.wholesaler, 'inventory-grid');
  }

  /** Balance bars are weeks, so the clicked period key stages directly. */
  onBalanceBarClick(event: { period: string }): void {
    this.selection.stage('period', event.period, 'inventory-balance');
  }

  ngAfterViewInit(): void {
    this.drawDohChart();
  }

  ngOnDestroy(): void {
    this.dohChartInstance?.destroy();
    this.subs.forEach((s) => s.unsubscribe());
  }

  /** Re-aggregate from the 852 fact table for the current badge filters. A
   *  confirmed selection lands in the Wholesaler badge, so it is included here. */
  private refresh(): void {
    const d = deriveInventory(this.filters.filter('Inventory'));
    this.data = d;
    this.kpiCards = d.kpis;
    this.inventoryRows = d.rows;

    // Date columns move with the reporting window, so the column defs are
    // rebuilt rather than only the rows swapped.
    this.buildGridColumns();

    this.balanceBarData = d.balanceBar.map((x) => ({ period: x.period, value: x.doh }));
    this.balanceLineData = d.balanceLine.map((x) => ({
      period: x.period,
      series: x.metric,
      value: x.qty,
    }));

    // Chart.js keeps its own copy of the dataset, so the DOH chart is redrawn
    // rather than mutated.
    this.drawDohChart();
  }

  onSearch(term: string): void {
    this.searchText = term;
    this.grid?.onQuickFilterChanged(term);
  }

  /** The date of the grid's latest column — shown in the snapshot chart header. */
  get dohLatestDate(): string {
    const cols = this.data.dateColumns;
    return cols.length ? cols[cols.length - 1] : '';
  }

  private buildGridColumns(): void {
    this.inventoryColumnDefs = [
      {
        headerName: 'Wholesaler',
        field: 'wholesaler',
        cellRenderer: 'agGroupCellRenderer',
        pinned: 'left',
        minWidth: 200,
        flex: 1.4,
        cellClass: 'emd-grid-parent-cell',
        // Renders the literal "Total" label in the pinned bottom row.
        aggFunc: 'Grand Total',
      },
      {
        headerName: 'QTY On Hand',
        field: 'total',
        type: 'Number',
        minWidth: 110,
        flex: 1,
        cellClass: 'emd-grid-total-cell',
        aggFunc: 'sum',
      },
      ...this.data.dateColumns.map((label, i) => ({
        headerName: label,
        field: `d${i}`,
        type: 'Number',
        minWidth: 120,
        flex: 1,
        aggFunc: 'sum',
      })),
    ];

    this.inventoryChildColumnDefs = [
      {
        // Child rows are the distribution centres under each wholesaler.
        headerName: 'Location',
        field: 'wholesaler',
        colId: 'wholesaler',
        pinned: 'left',
        minWidth: 200,
        flex: 1.4,
        cellClass: 'emd-grid-child-cell',
      },
      {
        headerName: 'QTY On Hand',
        field: 'total',
        colId: 'total',
        minWidth: 110,
        flex: 1,
      },
      ...this.data.dateColumns.map((label, i) => ({
        headerName: label,
        field: `d${i}`,
        colId: `d${i}`,
        minWidth: 120,
        flex: 1,
      })),
    ];
  }

  // ── DOH (AVG) bar chart ─────────────────────────────────────────────────────

  private drawDohChart(): void {
    const canvas = this.dohChartRef?.nativeElement;
    if (!canvas) { return; }
    this.dohChartInstance?.destroy();

    const ctx = canvas.getContext('2d');
    if (!ctx) { return; }

    // The right-hand chart is the LATEST week's snapshot from the grid: each
    // wholesaler's value in the most recent date column, sorted high to low —
    // so it lines up with the grid's rightmost column. Captured once so the
    // value-label plugin below reads the same array even after a later filter.
    const lastIdx = this.data.dateColumns.length - 1;
    const bars = lastIdx < 0
      ? []
      : this.data.rows
          .map((r: any) => ({ name: r.wholesaler as string, value: Number(r[`d${lastIdx}`]) || 0 }))
          .sort((a, b) => b.value - a.value);

    this.dohChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: bars.map((d) => d.name),
        datasets: [
          {
            label: 'DOH (AVG)',
            data: bars.map((d) => d.value),
            backgroundColor: EMD_COLORS.doh,
            borderRadius: 3,
            barPercentage: 0.5,
            categoryPercentage: 0.7,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 18 } },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: EMD_COLORS.muted, font: { size: 9, family: 'Poppins' } },
          },
          y: {
            min: 0,
            suggestedMax: 20,
            grid: { color: EMD_COLORS.grid },
            ticks: { color: EMD_COLORS.muted, font: { size: 10, family: 'Poppins' }, stepSize: 10 },
          },
        },
      },
      plugins: [
        {
          // Value labels above each bar, as in the reference.
          id: 'emdBarValueLabels',
          afterDatasetsDraw: (chart: any) => {
            const { ctx: c } = chart;
            chart.getDatasetMeta(0).data.forEach((bar: any, i: number) => {
              c.save();
              c.fillStyle = EMD_COLORS.ink;
              c.font = `600 10px Poppins`;
              c.textAlign = 'center';
              c.fillText(String(bars[i].value), bar.x, bar.y - 6);
              c.restore();
            });
          },
        },
      ],
    });
  }
}
